import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, WrenchScrewdriverIcon, HashtagIcon, DocumentTextIcon, CalendarIcon, PencilSquareIcon, PlusCircleIcon } from '@heroicons/react/24/outline'

const toDatetimeLocal = (val) => {
    if (!val) return ''
    const str = String(val).trim()
    if (!str) return ''

    // Aceita formatos vindos do backend:
    // - "2023-01-25 08:00"
    // - "2023-01-25T08:00:00.000Z"
    // - "2023-01-25T08:00"
    if (str.includes('T')) return str.slice(0, 16)
    if (str.includes(' ')) return str.replace(' ', 'T').slice(0, 16)
    return ''
}

const inputCls = (f, errors) => `mt-1.5 block w-full px-4 py-2.5 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${errors?.[f] ? 'border-red-300 bg-red-50/50 focus:ring-red-400' : 'border-gray-200 bg-white focus:ring-brand-yellow focus:border-brand-yellow'
    }`

const Field = ({ id, label, icon: Icon, error, children }) => (
    <div>
        <label htmlFor={id} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            <Icon className="w-3.5 h-3.5" />{label}
        </label>
        {children}
        {error && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><span>⚠</span>{error}</p>}
    </div>
)

const ModalObra = ({ states, setters, actionCreate, actionEdit, empreendimentoId }) => {
    const [codigoObra, setCodigoObra] = useState('')
    const [nomeObra, setNomeObra] = useState('')
    const [descricao, setDescricao] = useState('')
    const [dataInicio, setDataInicio] = useState('')
    const [dataFimPrevisto, setDataFimPrevisto] = useState('')
    const [dataCadastro, setDataCadastro] = useState('')
    const [errors, setErrors] = useState({})

    const clearErr = (f) => setErrors(p => ({ ...p, [f]: null }))

    const validate = () => {
        const e = {}
        if (!codigoObra.trim()) e.codigoObra = 'Campo obrigatório'
        if (!nomeObra.trim()) e.nomeObra = 'Campo obrigatório'
        if (!dataInicio) e.dataInicio = 'Campo obrigatório'
        if (!dataFimPrevisto) e.dataFimPrevisto = 'Campo obrigatório'
        if (!dataCadastro) e.dataCadastro = 'Campo obrigatório'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = () => {
        if (!validate()) return
        const payload = {
            codigo_obra: codigoObra,
            nome_obra: nomeObra,
            descricao,
            data_inicio: dataInicio.replace('T', ' '),
            data_fim_previsto: dataFimPrevisto.replace('T', ' '),
            data_cadastro: dataCadastro.replace('T', ' '),
            empreendimento_id: empreendimentoId || states.Obras?.empreendimento_id,
        }
        if (states.isEdit) actionEdit(states.Obras?.id, payload)
        else actionCreate(payload)
    }

    useEffect(() => {
        if (!states.modalObras) {
            setCodigoObra(''); setNomeObra(''); setDescricao('')
            setDataInicio(''); setDataFimPrevisto(''); setDataCadastro(''); setErrors({})
        } else if (states.isEdit && states.Obras?.id) {
            const o = states.Obras
            setCodigoObra(o.codigo_obra || '')
            setNomeObra(o.nome_obra || '')
            setDescricao(o.descricao || '')
            setDataInicio(toDatetimeLocal(o.data_inicio))
            setDataFimPrevisto(toDatetimeLocal(o.data_fim_previsto))
            setDataCadastro(toDatetimeLocal(o.data_cadastro))
        }
    }, [states.modalObras, states.isEdit, states.Obras])

    const isEdit = states.isEdit

    return (
        <Transition.Root show={states.modalObras} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalObras(false)}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all">

                                {/* Header */}
                                <div className="relative bg-gray-900 px-6 pt-6 pb-14 overflow-hidden">
                                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-brand-yellow/10" />
                                    <div className="absolute -bottom-12 -left-6 w-32 h-32 rounded-full bg-brand-yellow/5" />
                                    <div className="relative flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center flex-shrink-0 shadow-lg">
                                                <WrenchScrewdriverIcon className="w-7 h-7 text-gray-900" />
                                            </div>
                                            <div>
                                                <span className={`inline-block mb-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest ${isEdit ? 'bg-amber-400/20 text-amber-300' : 'bg-green-400/20 text-green-300'}`}>
                                                    {isEdit ? 'Modo edição' : 'Nova obra'}
                                                </span>
                                                <Dialog.Title as="h3" className="text-xl font-bold text-white">{isEdit ? 'Editar Obra' : 'Nova Obra'}</Dialog.Title>
                                                <p className="text-sm text-gray-400 mt-0.5">{isEdit ? `Editando: ${states.Obras?.nome_obra}` : 'Cadastre uma nova obra no empreendimento'}</p>
                                            </div>
                                        </div>
                                        <button onClick={() => setters.setModalObras(false)} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Form */}
                                <div className="relative -mt-6 mx-6 z-10">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <Field id="codigoObra" label="Código" icon={HashtagIcon} error={errors.codigoObra}>
                                                <input id="codigoObra" type="text" value={codigoObra} onChange={e => { setCodigoObra(e.target.value); clearErr('codigoObra') }} placeholder="R0001" className={inputCls('codigoObra')} />
                                            </Field>
                                            <Field id="nomeObra" label="Nome da Obra" icon={WrenchScrewdriverIcon} error={errors.nomeObra}>
                                                <input id="nomeObra" type="text" value={nomeObra} onChange={e => { setNomeObra(e.target.value); clearErr('nomeObra') }} placeholder="Construção do Prédio A" className={inputCls('nomeObra')} />
                                            </Field>
                                        </div>

                                        <Field id="descricao" label="Descrição" icon={DocumentTextIcon} error={errors.descricao}>
                                            <textarea id="descricao" rows={2} value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descreva a obra..." className={`${inputCls('descricao')} resize-none`} />
                                        </Field>

                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <Field id="dataInicio" label="Data Início" icon={CalendarIcon} error={errors.dataInicio}>
                                                <input id="dataInicio" type="datetime-local" value={dataInicio} onChange={e => { setDataInicio(e.target.value); clearErr('dataInicio') }} className={inputCls('dataInicio')} />
                                            </Field>
                                            <Field id="dataFimPrevisto" label="Fim Previsto" icon={CalendarIcon} error={errors.dataFimPrevisto}>
                                                <input id="dataFimPrevisto" type="datetime-local" value={dataFimPrevisto} onChange={e => { setDataFimPrevisto(e.target.value); clearErr('dataFimPrevisto') }} className={inputCls('dataFimPrevisto')} />
                                            </Field>
                                            <Field id="dataCadastro" label="Data Cadastro" icon={CalendarIcon} error={errors.dataCadastro}>
                                                <input id="dataCadastro" type="datetime-local" value={dataCadastro} onChange={e => { setDataCadastro(e.target.value); clearErr('dataCadastro') }} className={inputCls('dataCadastro')} />
                                            </Field>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-end gap-3 px-6 pt-4 pb-6">
                                    <button type="button" onClick={() => setters.setModalObras(false)} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all">Cancelar</button>
                                    <button type="button" onClick={handleSubmit} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-md hover:shadow-lg transition-all hover:scale-105">
                                        {isEdit ? <><PencilSquareIcon className="w-4 h-4" /> Salvar Alterações</> : <><PlusCircleIcon className="w-4 h-4" /> Cadastrar Obra</>}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalObra
